import { useEffect } from "react";
import { findGuide } from "./data";
import { setPageMeta } from "../../lib/pageMeta";

interface Props {
  slug: string;
}

const SITE = "https://casethread.app";

function articleJsonLd(args: {
  title: string;
  description: string;
  slug: string;
  lastUpdated: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.title,
    description: args.description,
    author: { "@type": "Organization", name: "CaseThread" },
    publisher: {
      "@type": "Organization",
      name: "CaseThread",
      logo: {
        "@type": "ImageObject",
        url: `${SITE}/casethread-icon.png`,
      },
    },
    datePublished: args.lastUpdated,
    dateModified: args.lastUpdated,
    mainEntityOfPage: `${SITE}/guides/${args.slug}`,
    image: `${SITE}/og-image.png`,
  };
}

function formatLastUpdated(iso: string): string {
  // ISO is yyyy-mm-dd from the data file. Render as e.g. "April 23, 2026".
  // Constructing with a UTC-noon timestamp avoids timezone rollback that
  // would otherwise show the previous day in negative-UTC locales.
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function GuidePage({ slug }: Props) {
  const guide = findGuide(slug);

  useEffect(() => {
    if (!guide) {
      setPageMeta({
        title: "Guide not found | CaseThread",
        description: "The guide you are looking for does not exist.",
        canonical: `${SITE}/guides`,
      });
      return;
    }
    setPageMeta({
      title: `${guide.title} | CaseThread`,
      description: guide.description,
      canonical: `${SITE}/guides/${guide.slug}`,
      jsonLd: articleJsonLd({
        title: guide.title,
        description: guide.description,
        slug: guide.slug,
        lastUpdated: guide.lastUpdated,
      }),
    });
    // SPA navigation preserves the previous scroll position by default
    // — but a fresh guide page should start at the top, unless a hash
    // anchor is present (TOC deep-link from another page or external
    // referrer). The setTimeout defers past React's first paint so the
    // anchor target exists before we scroll to it.
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      window.setTimeout(() => {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "auto", block: "start" });
      }, 0);
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [guide]);

  if (!guide) {
    return (
      <div className="guides-page">
        <header className="guides-header">
          <a href="/welcome" className="guides-brand">
            <img
              src="/casethread-icon.png?v=2"
              alt=""
              width={28}
              height={28}
              className="guides-brand-icon"
            />
            <span>CaseThread</span>
          </a>
          <nav aria-label="Header">
            <a href="/guides">All guides</a>
          </nav>
        </header>
        <main className="guides-main">
          <h1>Guide not found</h1>
          <p>
            The guide you are looking for does not exist.{" "}
            <a href="/guides">See all guides.</a>
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="guides-page">
      <header className="guides-header">
        <a href="/welcome" className="guides-brand">
          <img
            src="/casethread-icon.png?v=2"
            alt=""
            width={28}
            height={28}
            className="guides-brand-icon"
          />
          <span>CaseThread</span>
        </a>
        <nav aria-label="Header">
          <a href="/guides">All guides</a>
        </nav>
      </header>

      <main className="guides-main">
        <article className="guide-article">
          <p className="guide-crumbs">
            <a href="/guides">Guides</a>
            <span aria-hidden> / </span>
            <span>{guide.title}</span>
          </p>
          <h1 className="guide-title">{guide.title}</h1>

          {guide.sections.length > 0 && (
            <nav className="guide-toc" aria-label="On this page">
              <div className="guide-toc-label">On this page</div>
              <ul>
                {guide.sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>{s.label}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div className="guide-body">{guide.body()}</div>

          <aside
            className="guide-cta-box"
            aria-label="About CaseThread"
          >
            <h3>Make the paper trail work for you</h3>
            <p>
              CaseThread helps you organize your dispute correspondence
              automatically. Forward your emails and get a clear timeline,
              key facts, and a professional dossier.
            </p>
            <a href="/welcome" className="guide-cta-btn">
              Start free — casethread.app
            </a>
          </aside>

          <footer className="guide-foot">
            <p className="guide-updated">
              Last updated {formatLastUpdated(guide.lastUpdated)}
            </p>
            <p className="guide-disclaimer">
              CaseThread is a correspondence and documentation tool, not a
              law firm. This guide is general information, not legal advice.
              For advice on your specific situation, consult a qualified
              attorney or solicitor.
            </p>
          </footer>
        </article>
      </main>

      <footer className="guides-footer">
        <div className="guides-footer-inner">
          <span className="guides-footer-brand">© 2026 CaseThread</span>
          <nav aria-label="Footer">
            <a href="/welcome">About</a>
            <span aria-hidden> · </span>
            <a href="/guides">Guides</a>
            <span aria-hidden> · </span>
            <a href="/privacy">Privacy</a>
            <span aria-hidden> · </span>
            <a href="/terms">Terms</a>
            <span aria-hidden> · </span>
            <a href="/changelog">Changelog</a>
            <span aria-hidden> · </span>
            <a href="mailto:hello@casethread.app">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
